import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trophy, Coffee } from "lucide-react";
import { toast } from "sonner";

const IDLE_REASONS = ["No task assigned", "Material waiting", "Vehicle waiting", "QC waiting", "Invoice waiting", "System issue", "Break", "Other"];

export default function Workers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [idleLogs, setIdleLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [idleForm, setIdleForm] = useState({ user_id: "", reason: "", note: "" });
  const canRecordIdle = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    api.get("/workers/productivity").then((r) => setRows(r.data)).catch(() => {});
    if (canRecordIdle) {
      api.get("/idle").then((r) => setIdleLogs(r.data)).catch(() => {});
      api.get("/users").then((r) => setUsers(r.data.filter((u) => ["worker", "dispatch"].includes(u.role)))).catch(() => {});
    }
  }, [canRecordIdle]);

  useEffect(() => { load(); }, [load]);

  const recordIdle = async () => {
    try {
      await api.post("/idle", idleForm);
      toast.success("Idle time recorded");
      setOpen(false);
      setIdleForm({ user_id: "", reason: "", note: "" });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="workers-page">
      <SectionTitle
        right={
          canRecordIdle && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="record-idle-button"><Coffee className="w-4 h-4 mr-1" /> Record Idle Time</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border" data-testid="idle-dialog">
                <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Record Idle Time</DialogTitle><DialogDescription className="sr-only">Record worker idle time and reason</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Worker</Label>
                    <select
                      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={idleForm.user_id}
                      onChange={(event) => setIdleForm({ ...idleForm, user_id: event.target.value })}
                      data-testid="idle-worker-select"
                    >
                      <option value="">Select worker</option>
                      {users.map((worker) => (
                        <option key={worker.user_id} value={worker.user_id}>
                          {worker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Reason</Label>
                    <select
                      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={idleForm.reason}
                      onChange={(event) => setIdleForm({ ...idleForm, reason: event.target.value })}
                      data-testid="idle-reason-select"
                    >
                      <option value="">Select reason</option>
                      {IDLE_REASONS.map((reason) => (
                        <option key={reason} value={reason}>{reason}</option>
                      ))}
                    </select>
                  </div>
                  <div><Label>Note</Label><Input data-testid="idle-note-input" value={idleForm.note} onChange={(e) => setIdleForm({ ...idleForm, note: e.target.value })} /></div>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={recordIdle} data-testid="idle-submit-button">Record</Button>
                </div>
              </DialogContent>
            </Dialog>
          )
        }
      >
        Daily Worker Productivity Ranking
      </SectionTitle>
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="productivity-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Rank", "Worker", "Tasks Done", "Qty Handled", "Productive Hrs", "Qty / Man-hour", "Idle (min)", "Delayed Tasks"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.user_id} className="border-b border-border/50 hover:bg-accent/40 transition-colors duration-150" data-testid={`worker-row-${p.user_id}`}>
                <td className="px-4 py-3">
                  <span className={`metric font-bold flex items-center gap-1 ${i === 0 ? "text-signal-amber" : "text-muted-foreground"}`}>
                    {i === 0 && <Trophy className="w-4 h-4" />} {i + 1}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 metric">{p.tasks}</td>
                <td className="px-4 py-3 metric">{p.qty.toLocaleString()}</td>
                <td className="px-4 py-3 metric">{p.productive_hrs}</td>
                <td className="px-4 py-3 metric text-signal-green font-bold">{p.per_hour.toLocaleString()}</td>
                <td className={`px-4 py-3 metric ${p.idle_minutes > 120 ? "text-signal-red font-bold" : ""}`}>{p.idle_minutes}</td>
                <td className={`px-4 py-3 metric ${p.delayed > 0 ? "text-signal-amber font-bold" : ""}`}>{p.delayed}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No completed tasks yet today</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground max-w-2xl">
        Productivity = quantity handled ÷ productive hours (8-hour shift basis for idle time). This ranking is an
        operational indicator only — always interpret with supervisor context, not as sole basis for disciplinary action.
      </p>

      {canRecordIdle && idleLogs.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid="idle-logs-table">
          <h3 className="text-lg font-semibold uppercase tracking-tight px-5 pt-4">Idle Time Log</h3>
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="border-b border-border text-left">
                {["Time", "Worker", "Reason", "Note", "Recorded By"].map((h) => (
                  <th key={h} className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {idleLogs.map((l) => (
                <tr key={l.idle_id} className="border-b border-border/50">
                  <td className="px-4 py-2.5 metric text-xs">{fmtDT(l.at)}</td>
                  <td className="px-4 py-2.5 font-medium">{l.user_name}</td>
                  <td className="px-4 py-2.5">{l.reason}</td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">{l.note || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{l.recorded_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
